import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Copy, Check, ShieldCheck, Globe, Trash2 } from "lucide-react";
import ApiClient from "../utils/api";
import { useToast } from "../context/ToastContext";
import DeleteConfirmModal from "../components/DeleteConfirmModal";

const UNCHANGED_KEY = "__UNCHANGED__";
const MASK_DISPLAY = "••••••••••••••••••••";

const FreshchatConfig = ({ activeAiAgentId }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [freshchatConfig, setFreshchatConfig] = useState({
    api_token: "",
    freshchat_url: "",
    is_active: false,
  });
  const [webhookUrl, setWebhookUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      const response = await ApiClient.request("/freshchat/config", { aiAgentId: activeAiAgentId });
      if (response.success && response.config) {
        setFreshchatConfig({
          api_token: response.config.api_token ? UNCHANGED_KEY : "",
          freshchat_url: response.config.freshchat_url || "",
          is_active: response.config.is_active || false,
        });
        setWebhookUrl(response.config.webhook_url || "");
      }
    } catch (error) {
      console.error("Failed to load config:", error);
    }
  }, [activeAiAgentId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const saveConfig = async () => {
    if (!freshchatConfig.api_token.trim()) {
      showToast("Please enter your API token", "error");
      return;
    }

    if (!freshchatConfig.freshchat_url.trim()) {
      showToast("Please enter your Freshchat URL", "error");
      return;
    }

    try {
      setSaving(true);
      const response = await ApiClient.request("/freshchat/config", {
        aiAgentId: activeAiAgentId,
        method: "PUT",
        body: JSON.stringify(freshchatConfig),
      });

      if (response.success) {
        setWebhookUrl(response.webhook_url);
        showToast("Freshchat configuration saved successfully!", "success");
      }
    } catch (error) {
      // Error handled by ApiClient global toast
    } finally {
      setSaving(false);
    }
  };

  const deleteConfig = async () => {
    try {
      setSaving(true);
      const response = await ApiClient.request("/ai-agents/integrations/freshchat", {
        method: "DELETE",
      });
      if (response.success) {
        setFreshchatConfig({
          api_token: "",
          freshchat_url: "",
          is_active: false,
        });
        setWebhookUrl("");
        showToast("Freshchat integration deleted successfully!", "success");
      }
    } catch (error) {
      // handled globally
    } finally {
      setSaving(false);
      setIsDeleteModalOpen(false);
    }
  };

  const getDisplayWebhookUrl = () => {
    if (!webhookUrl) return "";
    let path = webhookUrl;
    if (webhookUrl.startsWith("http://") || webhookUrl.startsWith("https://")) {
      try {
        const urlObj = new URL(webhookUrl);
        path = urlObj.pathname + urlObj.search;
      } catch (e) {}
    }
    const baseUrl = process.env.REACT_APP_API_BASE_URL || window.location.origin;
    let cleanBase = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
    let cleanPath = path.startsWith("/") ? path : "/" + path;
    if (cleanBase.endsWith("/api") && cleanPath.startsWith("/api")) {
      cleanBase = cleanBase.slice(0, -4);
    }
    return cleanBase + cleanPath;
  };

  const displayUrl = getDisplayWebhookUrl();

  const copyWebhookUrl = async () => {
    try {
      await navigator.clipboard.writeText(displayUrl);
      setCopied(true);
      showToast("Webhook URL copied to clipboard!", "success");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      showToast("Failed to copy URL", "error");
    }
  };

  const toggleActive = async () => {
    if (!freshchatConfig.is_active) {
      if (!freshchatConfig.api_token?.trim() || !freshchatConfig.freshchat_url?.trim()) {
        showToast("Please enter and save your API token and URL first", "error");
        return;
      }
      if (freshchatConfig.api_token !== UNCHANGED_KEY) {
        showToast("Please save your configuration changes before activating", "warning");
        return;
      }
    }

    try {
      const newActiveState = !freshchatConfig.is_active;
      const response = await ApiClient.request("/freshchat/config", {
        aiAgentId: activeAiAgentId,
        method: "PUT",
        body: JSON.stringify({ is_active: newActiveState }),
      });

      if (response.success) {
        setFreshchatConfig((prev) => ({ ...prev, is_active: response.active }));
        showToast(response.message, "success");
      }
    } catch (error) {
      // Error handled by ApiClient global toast
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif] animate-slideIn">
      <div className="w-full max-w-7xl mx-auto px-4 py-5 sm:px-6 sm:py-6 lg:px-8 space-y-4 sm:space-y-6">
        
        {/* Top Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 sm:pb-5 border-b border-zinc-100 gap-3 sm:gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/integrations")}
              className="p-1.5 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-colors"
              title="Back to Integrations"
            >
              <ArrowLeft size={16} />
            </button>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight">Freshchat Integration</h1>
              <p className="text-xs text-zinc-500 mt-0.5">
                Bridge your AI assistant with Freshchat's omni-channel customer support.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2.5 self-start sm:self-auto pl-9 sm:pl-0">
            <span className={`px-2 py-0.5 rounded text-[10px] font-semibold tracking-wide uppercase border ${
              freshchatConfig.is_active
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-zinc-50 text-zinc-500 border-zinc-200"
            }`}>
              {freshchatConfig.is_active ? "Active" : "Disabled"}
            </span>
            <button
              type="button"
              onClick={toggleActive}
              className={`w-8 h-4.5 rounded-full transition-colors relative flex-shrink-0 ${
                freshchatConfig.is_active ? "bg-zinc-900" : "bg-zinc-200"
              }`}
              title={freshchatConfig.is_active ? "Deactivate integration" : "Activate integration"}
            >
              <div
                className={`absolute top-0.5 w-3.5 h-3.5 bg-white rounded-full transition-transform ${
                  freshchatConfig.is_active ? "left-4" : "left-0.5"
                }`}
              />
            </button>
          </div>
        </div>

        {/* Credentials Card */}
        <div className="bg-white rounded-xl border border-zinc-200 p-4 sm:p-6 space-y-5">
          <div className="flex items-center gap-2.5 pb-3 border-b border-zinc-100">
            <ShieldCheck className="text-zinc-800 shrink-0" size={18} />
            <div>
              <h2 className="text-xs font-semibold text-zinc-900 uppercase tracking-wider">Account Credentials</h2>
              <p className="text-[11px] text-zinc-500">Configure your Freshchat data center endpoint and admin token</p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Freshchat URL */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-medium text-zinc-700">
                  Freshchat API Data Center URL
                </label>
                <div className="flex items-center gap-1.5 text-[10px]">
                  <span className="text-zinc-400">Presets:</span>
                  <button
                    type="button"
                    onClick={() => setFreshchatConfig((prev) => ({ ...prev, freshchat_url: "https://api.in.freshchat.com" }))}
                    className="px-1.5 py-0.5 rounded bg-zinc-100 hover:bg-zinc-200 text-zinc-700 transition-colors font-medium"
                  >
                    India
                  </button>
                  <button
                    type="button"
                    onClick={() => setFreshchatConfig((prev) => ({ ...prev, freshchat_url: "https://api.freshchat.com" }))}
                    className="px-1.5 py-0.5 rounded bg-zinc-100 hover:bg-zinc-200 text-zinc-700 transition-colors font-medium"
                  >
                    US
                  </button>
                  <button
                    type="button"
                    onClick={() => setFreshchatConfig((prev) => ({ ...prev, freshchat_url: "https://api.eu.freshchat.com" }))}
                    className="px-1.5 py-0.5 rounded bg-zinc-100 hover:bg-zinc-200 text-zinc-700 transition-colors font-medium"
                  >
                    EU
                  </button>
                  <button
                    type="button"
                    onClick={() => setFreshchatConfig((prev) => ({ ...prev, freshchat_url: "https://api.au.freshchat.com" }))}
                    className="px-1.5 py-0.5 rounded bg-zinc-100 hover:bg-zinc-200 text-zinc-700 transition-colors font-medium"
                  >
                    AU
                  </button>
                </div>
              </div>
              <div className="relative">
                <input
                  type="url"
                  value={freshchatConfig.freshchat_url}
                  onChange={(e) =>
                    setFreshchatConfig({
                      ...freshchatConfig,
                      freshchat_url: e.target.value,
                    })
                  }
                  placeholder="https://api.in.freshchat.com"
                  className="w-full px-3.5 py-2 text-xs border border-zinc-200 rounded-lg bg-zinc-50/50 focus:bg-white focus:outline-none focus:border-zinc-900 text-zinc-900 font-mono transition-colors"
                />
              </div>
              <p className="text-[11px] text-zinc-500 mt-1">
                Must be an API data center URL (e.g. <code className="text-zinc-700 bg-zinc-100 px-1 py-0.5 rounded text-[10px]">https://api.in.freshchat.com</code>), not your account portal URL.
              </p>
            </div>

            {/* API Token */}
            <div>
              <label className="block text-xs font-medium text-zinc-700 mb-1.5">
                Admin API Token
              </label>
              <div className="relative">
                <input
                  type="password"
                  value={freshchatConfig.api_token === UNCHANGED_KEY ? MASK_DISPLAY : freshchatConfig.api_token}
                  onChange={(e) =>
                    setFreshchatConfig({
                      ...freshchatConfig,
                      api_token: e.target.value,
                    })
                  }
                  onFocus={() => {
                    if (freshchatConfig.api_token === UNCHANGED_KEY) {
                      setFreshchatConfig({ ...freshchatConfig, api_token: "" });
                    }
                  }}
                  placeholder={freshchatConfig.api_token === UNCHANGED_KEY ? MASK_DISPLAY : "Your Freshchat API token"}
                  className="w-full px-3.5 py-2 text-xs border border-zinc-200 rounded-lg bg-zinc-50/50 focus:bg-white focus:outline-none focus:border-zinc-900 text-zinc-900 font-mono transition-colors"
                />
              </div>
              <p className="text-[11px] text-zinc-400 mt-1">
                Found in <span className="text-zinc-600 font-medium">Settings → API Tokens</span> in your Freshchat dashboard.
              </p>
            </div>
          </div>

          <div className="pt-3 border-t border-zinc-100 flex flex-col-reverse sm:flex-row sm:items-center justify-between gap-2.5">
            {freshchatConfig.freshchat_url ? (
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(true)}
                disabled={saving}
                className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-40"
              >
                <Trash2 size={14} />
                <span>Disconnect Freshchat</span>
              </button>
            ) : <div />}

            <button
              type="button"
              onClick={saveConfig}
              disabled={saving || !freshchatConfig.api_token?.trim() || !freshchatConfig.freshchat_url?.trim()}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 disabled:opacity-40 text-xs font-medium transition-colors"
            >
              {saving ? (
                <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Save size={14} />
              )}
              <span>{saving ? "Saving..." : "Save Configuration"}</span>
            </button>
          </div>
        </div>

        {/* Webhook Endpoint Card */}
        {webhookUrl && (
          <div className="bg-white rounded-xl border border-zinc-200 p-4 sm:p-6 space-y-3">
            <div className="flex items-center gap-2.5 pb-3 border-b border-zinc-100">
              <Globe className="text-zinc-800 shrink-0" size={18} />
              <div>
                <h3 className="text-xs font-semibold text-zinc-900 uppercase tracking-wider">Freshchat Webhook URL</h3>
                <p className="text-[11px] text-zinc-500">Configure this endpoint in your Freshchat developer settings to receive incoming user chats</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 p-1.5 sm:p-2 bg-zinc-50 rounded-lg border border-zinc-200">
              <div className="font-mono text-[11px] sm:text-xs text-zinc-700 px-2 py-1 flex-1 break-all select-all min-w-0">
                {displayUrl}
              </div>
              <button
                type="button"
                onClick={copyWebhookUrl}
                className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex-shrink-0 ${
                  copied
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    : "bg-white text-zinc-800 border border-zinc-200 hover:bg-zinc-100"
                }`}
                title={copied ? "Copied!" : "Copy Webhook URL"}
              >
                {copied ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
                <span>{copied ? "Copied" : "Copy URL"}</span>
              </button>
            </div>

            <p className="text-[11px] text-zinc-400">
              Paste this URL into Freshchat under <span className="text-zinc-600 font-medium">Settings → Webhooks</span> to stream incoming conversations to this agent.
            </p>
          </div>
        )}
      </div>

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={deleteConfig}
        title="Disconnect Freshchat?"
        message="Are you sure you want to disconnect this integration? All credentials will be cleared and real-time webhook sync will stop."
        confirmText="Disconnect"
        isLoading={saving}
      />
    </div>
  );
};

export default FreshchatConfig;
